import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query';
import { roleService } from '../../services/admin/roles.service';
import type {
  Role,
  CreateRoleRequest,
  UpdateRoleRequest,
  RoleQueryParams,
} from '../../services/admin/role.types';
import type { PagedResult } from '../../services/base/base.service';

/**
 * Query keys for role-related queries
 * Centralized to ensure consistency across the app
 */
export const roleKeys = {
  all: ['roles'] as const,
  lists: () => [...roleKeys.all, 'list'] as const,
  list: (params: RoleQueryParams) => [...roleKeys.lists(), params] as const,
  details: () => [...roleKeys.all, 'detail'] as const,
  detail: (id: string) => [...roleKeys.details(), id] as const,
  byName: (name: string) => [...roleKeys.all, 'byName', name] as const,
  userCount: (id: string) => [...roleKeys.all, 'userCount', id] as const,
};

/**
 * Hook to fetch paginated list of roles
 *
 * @param params - Query parameters for filtering, sorting, and pagination
 * @returns React Query result with roles data
 *
 * @example
 * ```tsx
 * function RolesList() {
 *   const { data, isLoading, error } = useRoles({
 *     filter: 'admin',
 *     sorting: 'name asc',
 *     skipCount: 0,
 *     maxResultCount: 10
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return (
 *     <div>
 *       {data.items.map(role => (
 *         <RoleCard key={role.id} role={role} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useRoles(
  params: RoleQueryParams = {}
): UseQueryResult<PagedResult<Role>, Error> {
  return useQuery({
    queryKey: roleKeys.list(params),
    queryFn: () => roleService.getRoles(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch single role by ID
 *
 * @param id - Role ID
 * @param options - Query options
 * @returns React Query result with role data
 *
 * @example
 * ```tsx
 * function RoleDetails({ roleId }: { roleId: string }) {
 *   const { data: role, isLoading } = useRole(roleId);
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       <h1>{role.name}</h1>
 *       <p>Default: {role.isDefault ? 'Yes' : 'No'}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useRole(
  id: string,
  options?: { enabled?: boolean }
): UseQueryResult<Role, Error> {
  return useQuery({
    queryKey: roleKeys.detail(id),
    queryFn: () => roleService.getRole(id),
    enabled: options?.enabled !== false && !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch role by name
 *
 * @param name - Role name
 * @param options - Query options
 * @returns React Query result with role data
 *
 * @example
 * ```tsx
 * const { data: adminRole } = useRoleByName('Admin');
 * ```
 */
export function useRoleByName(
  name: string,
  options?: { enabled?: boolean }
): UseQueryResult<Role | null, Error> {
  return useQuery({
    queryKey: roleKeys.byName(name),
    queryFn: () => roleService.getRoleByName(name),
    enabled: options?.enabled !== false && !!name,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to get user count for a role
 *
 * @param roleId - Role ID
 * @returns React Query result with user count
 *
 * @example
 * ```tsx
 * const { data: userCount } = useRoleUserCount(roleId);
 * ```
 */
export function useRoleUserCount(
  roleId: string,
  options?: { enabled?: boolean }
): UseQueryResult<number, Error> {
  return useQuery({
    queryKey: roleKeys.userCount(roleId),
    queryFn: () => roleService.getRoleUsers(roleId),
    enabled: options?.enabled !== false && !!roleId,
    staleTime: 1000 * 60 * 2, // 2 minutes (more volatile data)
  });
}

/**
 * Hook to create a new role
 *
 * @returns Mutation result with create function
 *
 * @example
 * ```tsx
 * function CreateRoleForm() {
 *   const createRole = useCreateRole();
 *
 *   const handleSubmit = async (data: CreateRoleRequest) => {
 *     try {
 *       const role = await createRole.mutateAsync(data);
 *       toast.success('Role created successfully');
 *       navigate(`/roles/${role.id}`);
 *     } catch (error) {
 *       toast.error('Failed to create role');
 *     }
 *   };
 *
 *   return <RoleForm onSubmit={handleSubmit} isSubmitting={createRole.isPending} />;
 * }
 * ```
 */
export function useCreateRole(): UseMutationResult<
  Role,
  Error,
  CreateRoleRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRoleRequest) => roleService.createRole(data),
    onSuccess: (newRole) => {
      // Invalidate roles list to refetch
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });

      // Optimistically add to cache
      queryClient.setQueryData(roleKeys.detail(newRole.id), newRole);
    },
  });
}

/**
 * Hook to update an existing role
 *
 * @returns Mutation result with update function
 *
 * @example
 * ```tsx
 * function EditRoleForm({ role }: { role: Role }) {
 *   const updateRole = useUpdateRole();
 *
 *   const handleSubmit = async (data: UpdateRoleRequest) => {
 *     try {
 *       await updateRole.mutateAsync({ id: role.id, data });
 *       toast.success('Role updated successfully');
 *     } catch (error) {
 *       toast.error('Failed to update role');
 *     }
 *   };
 *
 *   return <RoleForm onSubmit={handleSubmit} defaultValues={role} />;
 * }
 * ```
 */
export function useUpdateRole(): UseMutationResult<
  Role,
  Error,
  { id: string; data: UpdateRoleRequest }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => roleService.updateRole(id, data),
    onSuccess: (updatedRole, variables) => {
      // Update the specific role in cache
      queryClient.setQueryData(roleKeys.detail(variables.id), updatedRole);

      // Invalidate lists to refetch with updated data
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
  });
}

/**
 * Hook to delete a role
 *
 * @returns Mutation result with delete function
 *
 * @example
 * ```tsx
 * function DeleteRoleButton({ role }: { role: Role }) {
 *   const deleteRole = useDeleteRole();
 *
 *   const handleDelete = async () => {
 *     if (!confirm(`Delete role "${role.name}"?`)) return;
 *
 *     try {
 *       await deleteRole.mutateAsync(role.id);
 *       toast.success('Role deleted successfully');
 *       navigate('/roles');
 *     } catch (error) {
 *       toast.error('Failed to delete role');
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleDelete} disabled={deleteRole.isPending}>
 *       Delete Role
 *     </Button>
 *   );
 * }
 * ```
 */
export function useDeleteRole(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => roleService.deleteRole(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: roleKeys.detail(deletedId) });

      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
    },
  });
}
